import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import Papa from 'papaparse';
import { deriveCanonicalEvidenceMetrics } from '@canonical-process';
import { createAssessmentRuntimeInput } from '@ingestion-core';
import { defaultColumnRoles, inferCsvMapping, ingestCsvEventRows, ingestCsvWorkItemRows } from '@csv-adapter';
import OrganizationContextStep,{EMPTY_ORGANIZATION_CONTEXT} from './OrganizationContextStep';

const MAX_CASES = 50000;
const STEP_ORDER = ['source','details','organization','mapping','attributes','validate','run'];
const STEP_LABELS = ['Source','Model details','Organization context','Mapping','Attributes','Validate','Run'];

const ROLE_LABELS = {
  caseId: 'Case ID',
  caseKey: 'Case Key / Number',
  activity: 'Activity',
  timestamp: 'Event timestamp',
  resource: 'Resource / User',
  summary: 'Summary / Description',
  createdAt: 'Case created',
  closedAt: 'Case closed / resolved',
};

const MAPPING_TIPS = {"caseId": "A stable identifier shared by every event belonging to the same process instance. Required.", "caseKey": "Optional human-readable case number or key. If omitted, Case ID is used.", "activity": "The ordered process step represented by each event. This can be Status, Stage, Action, Event Type, Transaction or another meaningful step.", "timestamp": "The date/time the activity occurred. Used to order events and calculate lead time, waiting time and other duration metrics.", "resource": "Optional person, team, queue, system or resource responsible for the event.", "summary": "Optional case title or description. Useful context for investigation and AI advisory.", "createdAt": "Optional explicit case creation time. If omitted for event logs, the earliest event can define the case start.", "closedAt": "Optional explicit case completion time. If omitted for event logs, the latest event can define the case end."};

function fmt(n) {
  try { return new Intl.NumberFormat().format(Number(n || 0)); } catch { return String(n || 0); }
}
function FieldLabel({ children, tip }) {
  return <span className="field-label-with-help">{children}{tip && <span className="field-tip" title={tip} aria-label={tip}><Info size={13}/></span>}</span>;
}

export default function CsvAssessmentBuilder({ onSave, onRunProcessAdvisor, repository, initial, refreshSource = false, accessStatus = null, onSelectRestSource, startWithCsv = false }) {
  const initialSource = initial?.source || {};
  const [sourceChosen,setSourceChosen] = useState(!!(refreshSource || initial || startWithCsv));
  const [fileName,setFileName] = useState(initialSource.fileName || '');
  const [rows,setRows] = useState([]);
  const [headers,setHeaders] = useState([]);
  const [mapping,setMapping] = useState(initialSource.mapping || {});
  const [columnRoles,setColumnRoles] = useState(initialSource.columnRoles || {});
  const [attributeNotes,setAttributeNotes] = useState(initialSource.attributeNotes || {});
  const [mode,setMode] = useState(initialSource.mode || 'event-log');

  const [modelName,setModelName] = useState(initial?.name || 'CSV Process Model');
  const [processType,setProcessType] = useState(initial?.processType || 'Imported Process');
  const [category,setCategory] = useState(initial?.category || '');
  const [description,setDescription] = useState(initial?.description || '');
  const [sourceSystem,setSourceSystem] = useState(initialSource.sourceSystem || '');
  const [businessObjective,setBusinessObjective] = useState(initial?.businessObjective || '');
  const [entityLabel,setEntityLabel] = useState(initialSource.entityLabel || 'Case');
  const [activityLabel,setActivityLabel] = useState(initialSource.activityLabel || 'Activity');
  const [organizationContext,setOrganizationContext] = useState(()=>({...EMPTY_ORGANIZATION_CONTEXT,...(initial?.organizationContext||{})}));

  const [ingestion,setIngestion] = useState(null);
  const [runtime,setRuntime] = useState(refreshSource ? null : (initial?.runtime || initial?.latestRuntime || null));
  const [drag,setDrag] = useState(false);
  const [sourceStep,setSourceStep] = useState(refreshSource ? 'source' : (initial ? 'run' : 'source'));
  const [limitError,setLimitError] = useState('');
  const [runBusy,setRunBusy] = useState(false);
  const [runError,setRunError] = useState('');
  const [runAccessChoice,setRunAccessChoice] = useState('free');
  const freeLimit = Math.min(250, Number(accessStatus?.freeAssessment?.processingUnitLimit || accessStatus?.freeAssessment?.caseLimit || 250));
  const availableAssessments = Number(accessStatus?.availableAssessments ?? accessStatus?.assessmentCredits?.balance ?? 0);
  const existingPaidAssessment = ['assessment','assessment_credit'].includes(String(initial?.currentAccess?.source || initial?.accessSource || ''));
  const csvSubscription=accessStatus?.subscription?.active||(accessStatus?.subscriptions||[]).find(s=>s?.active&&s?.connectionType==='csv-file');
  // Workspace subscriptions cover CSV imports without per-assessment credits.
  const caseCount = Number(ingestion?.validation?.summary?.cases || 0);
  const [retainProcessData,setRetainProcessData] = useState(Boolean(initial?.persistence?.retainProcessData ?? initial?.persistence?.storageConsent ?? initial?.source?.retainProcessData ?? initial?.source?.storageConsent ?? false));

  const metrics=useMemo(
    ()=>runtime?.canonicalDataset ? deriveCanonicalEvidenceMetrics(runtime.canonicalDataset) : null,
    [runtime]
  );
  const additionalHeaders = useMemo(
    ()=>headers.filter(h=>!Object.values(mapping).includes(h)),
    [headers,mapping]
  );

  const resetDerived = () => { setIngestion(null); setRuntime(null); setLimitError(''); setRunError(''); };

  const parseParsed = (parsed,name) => {
    const h = parsed.meta.fields || [];
    const inferred = inferCsvMapping(h);
    setFileName(name);
    setRows(parsed.data || []);
    setHeaders(h);
    setMapping(inferred);
    setColumnRoles(defaultColumnRoles(h,inferred));
    setAttributeNotes(Object.fromEntries(h.map(col => [col, attributeNotes[col] || ''])));
    resetDerived();
    setSourceChosen(true);
    setSourceStep('details');
  };

  const parseText = (text,name='sample.csv') => {
    const parsed=Papa.parse(text,{header:true,skipEmptyLines:'greedy',transformHeader:h=>String(h).trim()});
    parseParsed(parsed,name);
  };

  const loadFile = file => {
    if(!file) return;
    Papa.parse(file,{
      header:true,
      skipEmptyLines:'greedy',
      transformHeader:h=>String(h).trim(),
      complete:parsed=>parseParsed(parsed,file.name)
    });
  };

  const changeMapping = (key,value) => {
    const next={...mapping,[key]:value};
    setMapping(next);
    setColumnRoles(defaultColumnRoles(headers,next));
    resetDerived();
  };

  const validateImport = () => {
    const model = {
      name:modelName,
      processType,
      category,
      description,
      filename:fileName,
      sourceSystem,
      businessObjective,
      entityLabel,
      activityLabel,
      activityType:activityLabel || 'Activity',
      organizationContext,
    };
    const args={rows,headers,mapping,columnRoles,attributeNotes,model,maxCases:MAX_CASES};
    const result=mode==='work-items' ? ingestCsvWorkItemRows(args) : ingestCsvEventRows(args);
    setIngestion(result);
    setRuntime(null);
    const caseLimitDiagnostic=(result?.diagnostics||[]).find(d=>d?.code==='case-limit-exceeded');
    setLimitError(caseLimitDiagnostic?.message||'');
    setSourceStep('validate');

    window.__PA_INGESTION__=result;
    window.__PA_CANONICAL__={
      dataset:result.dataset,
      cases:result.dataset.cases,
      events:result.dataset.events,
      validation:result.validation
    };
  };

  const sourceMetadata = () => ({
    type:'csv',
    label:'CSV upload',
    fileName,
    mode,
    sourceSystem,
    entityLabel,
    activityLabel,
    mapping,
    columnRoles,
    attributeNotes,
    rawFileStored:false,
    retainProcessData:Boolean(retainProcessData),
    storageConsent:Boolean(retainProcessData),
    retentionMode:retainProcessData ? 'workspace-data' : 'definition-only',
  });

  const buildAssessment = input => {
    const evidence = deriveCanonicalEvidenceMetrics(input.canonicalDataset);
    return {
      ...(initial || {}),
      name:modelName,
      processType,
      category,
      description,
      businessObjective,
      organizationContext,
      status:'ready',
      assessmentStatus:'complete',
      source:sourceMetadata(),
      persistence:{
        modelDefinitionSaved:true,
        retainProcessData:Boolean(retainProcessData),
        storageConsent:Boolean(retainProcessData),
        retentionMode:retainProcessData?'workspace-data':'definition-only',
        rawFileStored:false,
      },
      summary:{
        cases:input.canonicalDataset.cases.length,
        events:input.canonicalDataset.events.length,
        variants:evidence.variants,
      },
      runtime:input,
      latestRuntime:input,
      metrics:evidence,
    };
  };

  const run = async () => {
    if(runBusy || !ingestion?.validation?.valid || !repository) return;
    setRunBusy(true);
    setRunError('');
    try {
      // Persist the CSV model definition first. Credits are activated against this model ID.
      const definition = await repository.saveModel({
        id: initial?.id || undefined,
        name:modelName,processType,category,description,businessObjective,organizationContext,
        source:sourceMetadata(),sourceType:'csv',sourceLabel:fileName||'CSV upload',
        status:'draft',assessmentStatus:'not-run',
        summary:{configuredCases:caseCount,requestedCases:caseCount,cases:caseCount,events:Number(ingestion?.validation?.summary?.events||0)},
        persistence:{modelDefinitionSaved:true,retainProcessData:false,storageConsent:false,retentionMode:'definition-only',rawFileStored:false}
      });
      const modelId=definition?.id || initial?.id;
      if(!modelId) throw new Error('The CSV model could not be saved before processing.');
      const authInput={
        modelId,modelName,caseCount,processedUnitCount:caseCount,
        eventCount:Number(ingestion?.validation?.summary?.events||0),
        sourceConnectionId:null,storageRequested:Boolean(retainProcessData)
      };
      let authorization=await repository.authorizeAssessment(authInput);
      if(!authorization?.allowed) throw new Error(authorization?.message||'CSV processing was not authorized.');
      const allowedCount=Math.min(caseCount,Number(authorization.processingLimit||freeLimit),MAX_CASES);
      if(allowedCount<1) throw new Error('No CSV cases were authorized for processing.');
      const original=createAssessmentRuntimeInput(ingestion);
      const dataset=original.canonicalDataset;
      const chosenCases=dataset.cases.slice(0,allowedCount);
      const chosenIds=new Set(chosenCases.map(c=>String(c.id)));
      const chosenEvents=(dataset.events||[]).filter(e=>chosenIds.has(String(e.caseId)));
      const limitedDataset={...dataset,cases:chosenCases,events:chosenEvents,
        metadata:{...(dataset.metadata||{}),ingestion:{...(dataset.metadata?.ingestion||{}),
          requestedCaseCount:caseCount,processedCaseCount:chosenCases.length,
          processingLimit:allowedCount,partialProcessing:caseCount>allowedCount,accessSource:authorization.source}}};
      const input={...original,canonicalDataset:limitedDataset,
        validation:{...(original.validation||{}),summary:{...(original.validation?.summary||{}),
          cases:chosenCases.length,events:chosenEvents.length,requestedCases:caseCount,
          processedCases:chosenCases.length,processingLimit:allowedCount,partialProcessing:caseCount>allowedCount}}};
      setRuntime(input);
      window.__PA_WEB_RUNTIME__=input;
      window.__PA_CANONICAL__={dataset:limitedDataset,cases:chosenCases,events:chosenEvents,metrics:deriveCanonicalEvidenceMetrics(limitedDataset)};
      await onRunProcessAdvisor?.({...buildAssessment(input),id:modelId,accessSource:authorization.source,
        accessAuthorizationId:authorization.authorizationId,entitlement:authorization,
        summary:{cases:chosenCases.length,events:chosenEvents.length,requestedCases:caseCount,
          processedCases:chosenCases.length,processingLimit:allowedCount,
          partialProcessing:caseCount>allowedCount,accessSource:authorization.source}});
    } catch(e) {
      setRunError(e?.message||'Unable to authorize CSV processing.');
    } finally {setRunBusy(false);}
  };

  const order = STEP_ORDER.indexOf(sourceStep);
  const canContinueSource = rows.length > 0;
  const requiredMappingsReady = mode === 'event-log'
    ? Boolean(mapping.caseId && mapping.activity && mapping.timestamp)
    : Boolean(mapping.caseId && (mapping.createdAt || mapping.timestamp));

  if (!sourceChosen) {
    return <>
      <div className="page-head"><div><h1>New model</h1><p>Choose the source that will define this process model.</p></div></div>
      <div className="source-grid">
        <button className="source-card active" onClick={()=>setSourceChosen(true)}>
          <span className="source-badge">Available</span><div className="source-icon">CSV</div>
          <h3>Upload CSV</h3><p>Event-log or case-record format. Map any activity field into the canonical process model.</p>
          <span className="source-action">Continue →</span>
        </button>
        <button className="source-card" onClick={()=>onSelectRestSource?.('jira-rest')}>
          <span className="source-badge neutral">REST</span><div className="source-icon">J</div><h3>Jira REST</h3><p>Connect without installing the Forge app. Credentials remain server-side and reusable across models.</p><span className="source-action">Choose source →</span>
        </button>
        <button className="source-card" onClick={()=>onSelectRestSource?.('servicenow-rest')}>
          <span className="source-badge neutral">REST</span><div className="source-icon">SN</div><h3>ServiceNow REST</h3><p>Connect to ServiceNow using a secure reusable workspace connection.</p><span className="source-action">Choose source →</span>
        </button>
      </div>
    </>;
  }

  return <>
    <div className="page-head">
      <div>
        <h1>{refreshSource?'Refresh model':initial?'Model':'New model'}</h1>
        <p>{refreshSource?'Replace the source data and rebuild the canonical model.':'Configure the source, mapping and attributes before running Process Advisor.'}</p>
      </div>
      {!initial && <button className="pa-secondary" onClick={()=>setSourceChosen(false)}>Change source</button>}
    </div>

    <div className="wizard-steps wizard-steps-six">
      {STEP_LABELS.map((label,i)=><button type="button" key={label} className={`wizard-step ${i<=order?'active':''} ${i===order?'current':''}`} onClick={()=>{
        if(i===0 || (i===1 && canContinueSource) || (i>=2 && canContinueSource)) setSourceStep(STEP_ORDER[i]);
      }}><span>{i+1}</span>{label}</button>)}
    </div>

    {sourceStep==='source' && <section className="panel">
      <div className="panel-head"><div><h2>CSV source</h2><p>Upload the source file and tell Process Advisor how the rows are structured.</p></div></div>
      <div className="panel-body">
        <div className="form-grid two">
          <label><FieldLabel tip="Choose Event log when each row is an activity/event. Choose Case records when each row represents one case or work item.">Import type</FieldLabel><select value={mode} onChange={e=>{setMode(e.target.value);resetDerived();}}>
            <option value="event-log">Event log — one row per event</option>
            <option value="work-items">Case records — one row per case</option>
          </select></label>
          <div className="field-help-card"><b>Maximum model size</b><span>{fmt(MAX_CASES)} cases per model. Event rows may exceed this count.</span></div>
        </div>
        <div className={`pa-upload ${drag?'drag':''}`} onDragOver={e=>{e.preventDefault();setDrag(true)}} onDragLeave={()=>setDrag(false)} onDrop={e=>{e.preventDefault();setDrag(false);loadFile(e.dataTransfer.files?.[0])}}>
          <b>Drop a CSV here</b><span>or choose a file from your computer</span>
          <label className="pa-primary">Choose CSV<input type="file" accept=".csv,text/csv" hidden onChange={e=>loadFile(e.target.files?.[0])}/></label>
        </div>
        {rows.length>0 && <div className="file-summary"><b>{fileName}</b><span>{fmt(rows.length)} rows · {headers.length} columns</span></div>}
        <div className="button-row right"><button className="pa-primary" disabled={!canContinueSource} onClick={()=>setSourceStep('details')}>Continue</button></div>
      </div>
    </section>}

    {sourceStep==='details' && <section className="panel">
      <div className="panel-head"><div><h2>Model details</h2><p>Describe what this data represents. These details follow the model into Evidence, Advisor and Assessment.</p></div></div>
      <div className="panel-body">
        <div className="form-grid two">
          <label><FieldLabel tip="A clear business name for this reusable process model.">Model name *</FieldLabel><input value={modelName} onChange={e=>setModelName(e.target.value)}/></label>
          <label><FieldLabel tip="A broad business domain used to organise models and give AI advisory context, for example ITSM, Customer Service, Finance or Operations.">Category</FieldLabel><input value={category} onChange={e=>setCategory(e.target.value)} placeholder="e.g. ITSM, Customer Service, Finance"/></label>
          <label><FieldLabel tip="The business process being analysed. This is important context for AI recommendations.">Process</FieldLabel><input value={processType} onChange={e=>setProcessType(e.target.value)} placeholder="e.g. Incident Management"/></label>
          <label><FieldLabel tip="Optional source application or system that produced the CSV, for example ServiceNow, Jira, SAP or Salesforce. This helps AI interpret field names and operating context.">Source system</FieldLabel><input value={sourceSystem} onChange={e=>setSourceSystem(e.target.value)} placeholder="e.g. ServiceNow, Jira, SAP"/></label>
          <label><FieldLabel tip="What a single Case ID represents in the business process, for example Incident, Order, Claim, Request or Application.">Case / entity name</FieldLabel><input value={entityLabel} onChange={e=>setEntityLabel(e.target.value)} placeholder="e.g. Incident, Order, Claim"/></label>
          <label><FieldLabel tip="The business meaning of the mapped Activity column, for example Status, Stage, Action, Event Type or Transaction.">Activity name</FieldLabel><input value={activityLabel} onChange={e=>setActivityLabel(e.target.value)} placeholder="e.g. Status, Stage, Action, Event Type"/></label>
          <label className="form-span-two"><FieldLabel tip="Describe the scope, exclusions, operating context and anything an advisor should know before interpreting the process.">Description / scope</FieldLabel><textarea rows={3} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Scope, exclusions and business context"/></label>
        </div>
        <div className="button-row between"><button className="pa-secondary" onClick={()=>setSourceStep('source')}>Back</button><button className="pa-primary" disabled={!modelName.trim()} onClick={()=>setSourceStep('organization')}>Continue</button></div>
      </div>
    </section>}


    {sourceStep==='organization' && repository && <OrganizationContextStep
      value={organizationContext}
      onChange={setOrganizationContext}
      repository={repository}
      sourceSystem={sourceSystem||'CSV'}
      isExisting={Boolean(initial?.id)}
      modelContext={{name:modelName,category,processType,description,entityLabel}}
      onBack={()=>setSourceStep('details')}
      onContinue={()=>setSourceStep('mapping')}
    />}

    {sourceStep==='mapping' && <section className="panel">
      <div className="panel-head"><div><h2>Core column mapping</h2><p>Map the columns Process Advisor needs to reconstruct the process sequence.</p></div></div>
      <div className="panel-body">
        <div className="form-grid two">
          {Object.entries(ROLE_LABELS).map(([key,label])=><label key={key}>
            <FieldLabel tip={MAPPING_TIPS[key]}>{label}{(['caseId','activity','timestamp'].includes(key)&&mode==='event-log') || (key==='caseId'&&mode==='work-items') ? ' *':''}</FieldLabel>
            <select value={mapping[key]||''} onChange={e=>changeMapping(key,e.target.value)}>
              <option value="">— not mapped —</option>{headers.map(h=><option key={h} value={h}>{h}</option>)}
            </select>
          </label>)}
        </div>
        <div className="button-row between"><button className="pa-secondary" onClick={()=>setSourceStep('organization')}>Back</button><button className="pa-primary" disabled={!requiredMappingsReady} onClick={()=>setSourceStep('attributes')}>Continue to attributes</button></div>
      </div>
    </section>}

    {sourceStep==='attributes' && <section className="panel">
      <div className="panel-head"><div><h2>Characteristics &amp; attributes <span className="field-tip" title="Attributes give Process Advisor context beyond the process path. Case characteristics are best for segmentation and AI comparisons; event attributes are values that can change from one event to the next."><Info size={14}/></span></h2><p>Choose which additional fields should provide business context for Evidence, Segments and AI advisory.</p></div><span className="status-pill neutral">{additionalHeaders.length} columns</span></div>
      <div className="panel-body">
        <div className="attribute-help-strip">
          <span><b>Case characteristic</b> Best for stable context such as Priority, Category, Customer Type, Region or Assignment Group.</span>
          <span><b>Event attribute</b> Use when the value can change during the case, such as resource, channel or location at an event.</span>
          <span><b>Business meaning</b> Optional, but useful where a column name is ambiguous. It is carried into the canonical attribute definition for AI advisory.</span>
          <span><b>Ignore</b> Excludes fields that add no analytical or advisory value.</span>
        </div>
        {additionalHeaders.length ? <div className="attribute-role-grid">
          {additionalHeaders.map(h=><div className="attribute-role-row" key={h}>
            <div className="attribute-role-name"><b>{h}</b><span>{columnRoles[h]==='event-attribute'?'Changes by event · available for event analysis':columnRoles[h]==='ignore'?'Not imported':'Stable case context · available to Segments and AI'}</span></div>
            <input className="attribute-meaning" value={attributeNotes[h]||''} onChange={e=>setAttributeNotes({...attributeNotes,[h]:e.target.value})} placeholder="Optional business meaning for AI"/>
            <select value={columnRoles[h]||'case-attribute'} onChange={e=>setColumnRoles({...columnRoles,[h]:e.target.value})}>
              <option value="case-attribute">Case characteristic</option>
              {mode==='event-log' && <option value="event-attribute">Event attribute</option>}
              <option value="ignore">Ignore</option>
            </select>
          </div>)}
        </div> : <div className="detail-empty">All CSV columns are used in the core mapping.</div>}
        <div className="button-row between"><button className="pa-secondary" onClick={()=>setSourceStep('mapping')}>Back</button><button className="pa-primary" onClick={validateImport}>Validate model</button></div>
      </div>
    </section>}

    {sourceStep==='validate' && <section className="panel">
      <div className="panel-head"><div><h2>Canonical validation</h2><p>{ingestion ? `${fmt(ingestion.validation.summary.cases)} cases · ${fmt(ingestion.validation.summary.events)} events` : 'Validation stopped'}</p></div>
        <span className={`status-pill ${limitError || !ingestion?.validation?.valid ? 'danger':'success'}`}>{limitError || !ingestion?.validation?.valid ? 'Fix errors':'Ready'}</span>
      </div>
      <div className="panel-body">
        {limitError ? <div className="pa-alert error">{limitError}</div> : ingestion && <>
          <div className="metric-grid four compact">
            <div className="metric-card"><span>Cases</span><strong>{fmt(ingestion.validation.summary.cases)}</strong></div>
            <div className="metric-card"><span>Events</span><strong>{fmt(ingestion.validation.summary.events)}</strong></div>
            <div className="metric-card"><span>Warnings</span><strong>{ingestion.validation.counts.warning||0}</strong></div>
            <div className="metric-card"><span>Errors</span><strong>{ingestion.validation.counts.error||0}</strong></div>
          </div>
          {ingestion.validation.diagnostics.slice(0,20).map((d,i)=><div key={i} className={`pa-alert ${d.severity}`}>{d.message}</div>)}
        </>}
        <div className="button-row between"><button className="pa-secondary" onClick={()=>setSourceStep('attributes')}>Back</button><button className="pa-primary" disabled={!ingestion?.validation?.valid || !!limitError} onClick={()=>setSourceStep('run')}>Continue</button></div>
      </div>
    </section>}

    {sourceStep==='run' && <section className="panel">
      <div className="panel-head"><div><h2>Run Process Advisor</h2><p>Review the model definition and choose whether Process Advisor may retain the extracted process data.</p></div></div>
      <div className="panel-body">
        <div className="run-review-grid">
          <div><span>Model</span><b>{modelName}</b></div>
          <div><span>Category</span><b>{category || '—'}</b></div>
          <div><span>Process</span><b>{processType || '—'}</b></div>
          <div><span>Source system</span><b>{sourceSystem || '—'}</b></div>
          <div><span>Case</span><b>{entityLabel || 'Case'} · {mapping.caseId || '—'}</b></div>
          <div><span>Activity</span><b>{activityLabel || 'Activity'} · {mapping.activity || '—'}</b></div>
        </div>

        <div className="run-access-strip">
          <span><b>Model definition</b>Always saved to Models so you can reopen, edit and rerun it.</span>
          <span><b>Processing volume</b>{`Free trial: up to ${fmt(freeLimit)} cases · active subscription: full access`}</span>
        </div>

        <div className="panel" style={{padding:16,marginTop:12,marginBottom:12}}>
          <strong>{accessStatus?.subscription?.active?'Full processing access':'Free trial'}</strong>
          <p>{fmt(caseCount)} CSV cases detected. {accessStatus?.subscription?.active?'Your subscription covers the configured scope.':`The free trial processes up to ${fmt(freeLimit)} cases. Subscribe for full processing.`}</p>
        </div>
        {runError && <div className="pa-alert error" role="alert">{runError}</div>}
        <label className="storage-consent">
          <input type="checkbox" checked={retainProcessData} onChange={e=>setRetainProcessData(e.target.checked)}/>
          <span><b>Retain extracted process data in Process Advisor</b><small>Optional. If enabled, the canonical cases/events and selected attributes are stored so the model can reopen without reloading the source. If disabled, the model definition and assessment history are still saved, but the processed dataset is discarded after this session. The original CSV file is never stored.</small></span>
        </label>

        <div className="pa-alert info">Processing is covered by your subscription, or limited to 250 cases during the free trial.</div>

        <div className="button-row between">
          <button className="pa-secondary" onClick={()=>setSourceStep('validate')}>Back</button>
          <div className="button-row">
            <button className="pa-primary" disabled={runBusy || (!ingestion?.validation?.valid && !runtime)} onClick={run}>{runBusy?'Authorizing…':'Run Process Advisor'}</button>
          </div>
        </div>
      </div>
    </section>}
  </>;
}
